
import React, { useState, useEffect } from "react";
import {Container,Box,Typography,Button,Card,CardContent,Chip,IconButton,CircularProgress,Paper,} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import Footer from "../../Components/Common/Footer";
import { apiRequest } from '../../fetchSetup';


const STATUS_COLORS = {
  bill_generated: { bg: "#DBEAFE", text: "#2563EB", label: "Bill Generated" },
  confirmed: { bg: "#DBEAFE", text: "#2563EB", label: "Confirmed" },
  pending: { bg: "#FEF3C7", text: "#D97706", label: "Pending" },
  out_for_delivery: { bg: "#FEF3C7", text: "#D97706", label: "Out for delivery" },
  dispatched: { bg: "#E0E7FF", text: "#6366F1", label: "Dispatched" },
  delivered: { bg: "#D1FAE5", text: "#059669", label: "Delivered" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626", label: "Cancelled" },
};


const MyOrdersScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");

  const filters = [
    { id: "all", label: "All" },
    { id: "bill_generated", label: "Bill Ready" },
    { id: "confirmed", label: "Confirmed" },
    { id: "awaiting_bill", label: "Pending" },
    { id: "delivered", label: "Delivered" },
    { id: "out_for_delivery", label: "out for delivery" },
    { id: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (user?.email) {
      fetchOrders(user.email);
    }
  }, [user]);

  useEffect(() => {
    filterOrders();
  }, [selectedFilter, orders]);

  const loadUserData = async () => {
    const userId = sessionStorage.getItem("userId");
    const token = sessionStorage.getItem("token");

    if (!userId || !token) {
      setUser(null);
      return;
    }

    try {
      const userData = await fetchUserFromRedis(userId, token);
      setUser(userData || null);
    } catch (err) {
      console.error("Error fetching user from Redis:", err);
      setUser(null);
    }
  };

  const fetchOrders = async (email) => {
    try {
      setLoading(true);
      const endpoint = `${process.env.REACT_APP_GET_USER_MEDICINE_ORDERS}/${email}`;
      console.log("Fetching orders from:", endpoint);
      
      const response = await apiRequest(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Orders fetched:", data);

      if (data.orders && Array.isArray(data.orders)) {
        setOrders(data.orders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      alert("Failed to load orders. Please try again.");
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    if (user?.email) {
      fetchOrders(user.email);
    }
  };

  const filterOrders = () => {
    if (selectedFilter === "all") {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter((order) => order.order_status === selectedFilter));
    }
  };

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleOrderPress = (order) => {
    navigate("/order-details", { state: { orderId: order.order_id } });
  };
 
  const renderOrderCard = (order) => {
    const statusInfo = getStatusColor(order.order_status);
    const itemCount = order.items?.length || 0;
    const hasChildOrders = order.has_children && order.child_order_count > 0;
    
    return (
      <Card
        key={order.order_id}
        elevation={2}
        sx={{
          borderRadius: 3,
          mb: 2,
          overflow: "hidden",
          border:"1px solid #E5E7EB",
          "&:hover": {
            boxShadow: "0 3px 6px rgba(0,0,0,0.08)",
          },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Header Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {order.order_id}
              {hasChildOrders && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    bgcolor: "#006D77",
                    color: "white",
                    fontSize: 11,
                    fontWeight: 700,
                    px: 1,
                    py: 0.3,
                    borderRadius: 1,
                  }}
                >
                  {order.child_order_count} Split{order.child_order_count > 1 ? 's' : ''}
                </Box>
              )}
            </Typography>
            <Chip
              label={statusInfo.label}
              sx={{
                bgcolor: statusInfo.bg,
                color: statusInfo.text,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: "16px",
                px: 1.5,
                pointerEvents: "none",
              }}
            />
          </Box>
  
          {/* Order Meta Info */}
          <Typography
            sx={{
              fontSize: 14,
              color: "#121212",
              mb: 0.5,
            }}
          >
            Order date: {formatDate(order.created_at)}
          </Typography>
  
          <Typography
            sx={{
              fontSize: 16,
              color: "#006D77",
              fontWeight: 600,
              mb: 1,
            }}
          >
            {order.order_type === "pharmacy" ? "Pharmacy" : "Prescription"}
          </Typography>
  
          {/* Item List */}
          {itemCount > 0 && (
            <Box
              sx={{
                bgcolor: "#E5FDCF",
                borderRadius: 2,
                p: 1.5,
                mb: 1,
                border: "1px solid #D1FAE5",
              }}
            >
              {order.items?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.4, 
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    • {item.medicine_name} {item.strength ? item.strength : ""} x{" "}
                    {item.quantity}
                  </Typography>
                  <Box
              sx={{
                bgcolor: "#22577A",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                px: 1.5,
                py: 0.25,
                borderRadius: 4,
                display: "inline-block",
                minWidth: 40,
                textAlign: "center",
              }}
            >
              ₹{item.total_price.toFixed(2)}
            </Box>

                            </Box>
                          ))}
                        </Box>
                      )}
              
          {/* Total Amount */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #E5E7EB",
              pt: 1.5,
              mt: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                Total Amount
              </Typography>
              <Box
              sx={{
                bgcolor: "#22577A",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                display: "inline-block",
                textAlign: "center",
                minWidth: 60,
              }}
            >
              ₹{order.total_amount.toFixed(2)}
            </Box>
        
            </Box>
     
            
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOrderPress(order);
              }}
              sx={{
                borderColor: "#006D77",
                bgcolor: "#22577A",
                color: "#fff",
                borderRadius: 3,
                fontWeight: 700,
                fontSize: 13,
                textTransform: "none",
                px: 2.5,
                "&:hover": {
                  bgcolor: "#E0F2F1",
                },
              }}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 10,
      }}
    >
      <Typography sx={{ fontSize: 80, mb: 2 }}>📦</Typography>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: "#111827",
          mb: 1,
        }}
      >
        No Orders Found
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "#6B7280",
          textAlign: "center",
          mb: 3,
          px: 5,
        }}
      >
        {selectedFilter === "all"
          ? "You haven't placed any orders yet"
          : `No ${selectedFilter} orders found`}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate("/medicine-order-type")}
        sx={{
          bgcolor: "#006D77",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          textTransform: "none",
          px: 3,
          py: 1.5,
          "&:hover": {
            bgcolor: "#005A63",
          },
        }}
      >
        Browse Medicines
      </Button>
    </Box>
  );

  if (loading && !refreshing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#F9FAFB",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#006D77" }} />
        <Typography sx={{ mt: 2, fontSize: 16, color: "#6B7280" }}>
          Loading your orders...
        </Typography>
      </Box>
    );
  }

  return (
    <>
     ` <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB" }}>
      
     <Paper elevation={2} sx={{ bgcolor: "#fff", mb: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{py: { xs: 1.5, sm: 2, md: 3 },display: "flex",alignItems: "center",justifyContent: "space-between",gap: 1, }}>
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                flexShrink: 0,
                backgroundColor: "white",
                boxShadow: 1,
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                color: "#006D77",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.4rem", md: "2rem" },
                textAlign: "center",
                flex: 1,
              }}
            >
              My Medicine Orders
            </Typography>

                <IconButton onClick={onRefresh} disabled={refreshing} sx={{position: "absolute",right: 0, }} >
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Container>
          </Paper>

        <Paper
          elevation={0}
          sx={{
            bgcolor: "transparent",
            borderBottom: "1px solid #E5E7EB",
            py: 1.5,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {filters.map((filter) => (
                <Chip
                  key={filter.id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <span>{filter.label}</span>
                      {filter.id !== "all" && (
                        <Box
                          sx={{
                            bgcolor: selectedFilter === filter.id ? "#006D77" : "#006D77",
                            color: selectedFilter === filter.id ? "#fff" : "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            px: 0.55,
                            py: 0.55,
                            borderRadius: 1.25,
                            minWidth: 20,
                            textAlign: "center",
                          }}
                        >
                          {orders.filter((o) => o.order_status === filter.id).length}
                        </Box>
                      )}
                    </Box>
                  }
                  onClick={() => setSelectedFilter(filter.id)}
                  sx={{
                    bgcolor: selectedFilter === filter.id ? "#006D77" : "white",
                    color: selectedFilter === filter.id ? "#fff" : "black",
                    fontWeight: 600,
                    fontSize: 14,
                    border:
                      selectedFilter === filter.id ? "1px solid white" : "1px solid #006D77",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: selectedFilter === filter.id ? "#B2DFDB" : "#E5E7EB",
                    },
                  }}
                />
              ))}
            </Box>
          </Container>
        </Paper>

        {orders.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Paper
              elevation={1}
              sx={{
                flex: 1,
                bgcolor: "#006D77",
                p: 2,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#fff",
                  mb: 0.5,
                }}
              >
                {orders.length}
              </Typography>
              <Typography sx={{ fontSize: 16, color: "#fff" }}>
                Total Orders
              </Typography>
            </Paper>
            <Paper
              elevation={1}
              sx={{
                flex: 1,
                bgcolor: "#006D77",
                p: 2,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#fff",
                  mb: 0.5,
                }}
              >
                {orders.filter((o) => o.order_status === "delivered").length}
              </Typography>
              <Typography sx={{ fontSize: 16, color: "#fff" }}>
                Delivered
              </Typography>
            </Paper>
            {/* <Paper
              elevation={1}
              sx={{
                flex: 1,
                bgcolor: "#006D77",
                p: 2,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#fff",
                  mb: 0.5,
                }}
              >
                ₹
                {orders
                  .filter((o) => o.order_status === "delivered")
                  .reduce((sum, o) => sum + o.total_amount, 0)
                  .toFixed(0)}
              </Typography>
              <Typography sx={{ fontSize: 16, color: "#fff" }}>
                Total Spent
              </Typography>
            </Paper> */}
          </Box>
        </Container>
      )}

        <Container maxWidth="lg" sx={{ py: 2, pb: 4 }}>
          {filteredOrders.length === 0 ? renderEmptyState() : filteredOrders.map(renderOrderCard)}
        </Container>
        
      </Box>
      <Footer />
    </>
  );
};

export default MyOrdersScreen;